import React from 'react';
import classNames from 'classnames';

import { BaseBodyProps } from 'js/design_system/Body';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';
import 'app/assets/stylesheets/design_system/body.scss';

export type BaseBodyLinkProps = BaseBodyProps & {
  underline?: boolean;
  external?: boolean;
  bold?: boolean;
  trackClick?: TrackClickOptions;
};

type BodyLinkProps = BaseBodyLinkProps & React.ComponentPropsWithoutRef<'a'>;

/**
 * BodyLink component used for inline links within body text.
 *
 * Props:
 * - `size`: Controls the font-size and line-height of the text (default: 'md')
 *           Choose between 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * - `underline`: Whether the link should be underlined in its static state (default: false)
 * - `external`: Whether the link should act as an external link (default: false)
 * - `bold`: Whether the body link should have increased font weight in its static state (default: false)
 * - `trackClick`: Options for tracking clicks on this link
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { BodyLink } from 'js/design_system/link/BodyLink';
 *
 * <BodyLink href={#}>A simple link</BodyLink>
 * ```
 * ---
 *
 * ## Custom Size
 *
 * @example
 *
 * ```tsx
 * import { BodyLink } from 'js/design_system/link/BodyLink';
 *
 * <BodyLink href={#} size='lg'>Bigger body link</BodyLink>
 * ```
 * ---
 *
 * ## Underline while static
 * ```tsx
 * import { BodyLink } from 'js/design_system/link/BodyLink';
 *
 * <BodyLink href={#} underline>Underlined body link</BodyLink>
 * ```
 * ---
 *
 */
export const BodyLink = React.forwardRef<HTMLAnchorElement, BodyLinkProps>(
  (
    {
      size = 'md',
      bold = false,
      underline = false,
      external = false,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      rel: relProp,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames(
      className,
      `ds2-5-body-${size}${bold ? '-bold' : ''}`,
      `ds2-5-body-link`,
      {
        'ds2-5-body-link-underline': underline,
      },
    );

    const rel = [relProp, external ? 'noreferrer' : ''].filter(Boolean);

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      // This IS the design system implementation:
      // eslint-disable-next-line no-restricted-syntax
      <a
        {...restProps}
        className={classes}
        rel={rel.length > 0 ? rel.join(' ') : undefined}
        onClick={handleClick}
        ref={ref}
      >
        {children}
      </a>
    );
  },
);
